import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '@/api/client';

type Status = 'ok' | 'warning' | 'error' | 'unknown';

type HealthData = {
  status?: string;
  timestamp?: number;
  version?: string | null;
  environment?: string | null;
  uptime_seconds?: number;
  summary?: Record<string, any> | null;
  api?: {
    status?: string;
    uptime_seconds?: number;
    version?: string | null;
    environment?: string | null;
    memory?: Record<string, any>;
    cpu?: Record<string, any>;
    requests?: { in_flight?: number };
  } | null;
  websocket?: {
    state?: string;
    connections_active?: number;
    admin_connections?: number;
    agents_connected?: number;
    recent_reconnects?: number;
  } | null;
  agents?: {
    total?: number;
    connected?: number;
    disconnected?: number;
    with_error?: number;
    last_connected?: { agent_id?: string | null; timestamp?: string | null } | null;
  } | null;
  database?: {
    status?: string;
    engine?: string;
    ping_ms?: number;
    pool?: Record<string, any> | null;
    last_error?: string | null;
  } | null;
  system?: {
    hostname?: string;
    pid?: number;
    os?: string;
    arch?: string;
    load_average?: number[] | null;
    disk?: Record<string, any> | null;
  } | null;
};

export default function useSystemStatus(pollInterval = 30000) {
  const [status, setStatus] = useState<Status>('unknown');
  const [details, setDetails] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [health, setHealth] = useState<HealthData | null>(null);
  const mounted = useRef(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/health');
      if (!mounted.current) return;
      const data = res.data as HealthData;
      setHealth(data ?? null);

      // Map backend health to UI status
      // Map backend health to UI status
      const s = (data?.status || data?.api?.status || '').toLowerCase();
      if (s === 'healthy') setStatus('ok');
      else if (s === 'degraded') setStatus('warning');
      else if (s === 'unhealthy') setStatus('error');
      else setStatus('unknown');

      setDetails(`Backend: ${data?.status ?? data?.api?.status ?? 'unknown'}`);
      // collect any notable errors from checks/database/agents
      const newErrors: string[] = [];
      if (data?.database && (data.database.status === 'down' || data.database.last_error)) {
        newErrors.push(`Database: ${data.database.last_error ?? 'down'}`);
      }
      if (
        data?.agents &&
        typeof data.agents.with_error === 'number' &&
        data.agents.with_error > 0
      ) {
        newErrors.push(`Agents with error: ${data.agents.with_error}`);
      }
      // include websocket state issues
      if (data?.websocket && data.websocket.state && data.websocket.state !== 'connected') {
        newErrors.push(`WebSocket: ${data.websocket.state}`);
      }
      setErrors(newErrors);
    } catch (err) {
      if (!mounted.current) return;
      setStatus('error');
      const msg = err instanceof Error ? err.message : 'No se pudo obtener el estado del sistema.';
      setDetails(msg);
      setErrors([msg]);
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    startTransition(() => {
      void fetchStatus();
    });
    const id = setInterval(
      () =>
        startTransition(() => {
          void fetchStatus();
        }),
      pollInterval
    );
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [fetchStatus, pollInterval]);

  return {
    status,
    details,
    errors,
    health,
    refresh: fetchStatus,
  } as const;
}
